import {
  AR_SEARCH_MAX_LENGTH,
  EARLIEST_BUSINESS_DATE,
  isCalendarDate,
  isValidUuidV7,
  MAX_UUID_ARRAY_LENGTH,
} from '@nudool/shared';

import { parseListRouteSearch } from '../../hooks/use-list-table-helpers/index.js';
import { AR_REPORT_VIEWS, type ArReportSearch } from './ar-report-search.js';

export function parseIdList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const ids = [
    ...new Set(value.filter((id): id is string => typeof id === 'string' && isValidUuidV7(id))),
  ].slice(0, MAX_UUID_ARRAY_LENGTH);
  return ids.length > 0 ? ids : undefined;
}

// The router JSON-parses search params, so a typed `?q=1042` arrives as a number.
function parseSearchText(value: unknown): string | undefined {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const text = String(value).trim().slice(0, AR_SEARCH_MAX_LENGTH);
  return text === '' ? undefined : text;
}

// Shape-only and never throws, so a hand-edited URL falls back to the defaults.
export function parseArReportSearch(search: Record<string, unknown>): ArReportSearch {
  const parsed: ArReportSearch = {};

  // A future date is kept so the report can say why it will not run; the picker never offers one.
  if (
    typeof search.asOfDate === 'string' &&
    isCalendarDate(search.asOfDate) &&
    search.asOfDate >= EARLIEST_BUSINESS_DATE
  ) {
    parsed.asOfDate = search.asOfDate;
  }

  const brandIds = parseIdList(search.brandIds);
  if (brandIds) parsed.brandIds = brandIds;
  const stateIds = parseIdList(search.stateIds);
  if (stateIds) parsed.stateIds = stateIds;
  const termsIds = parseIdList(search.termsIds);
  if (termsIds) parsed.termsIds = termsIds;

  if (search.pastDueOnly === true) parsed.pastDueOnly = true;
  if (search.includeCredits === true) parsed.includeCredits = true;
  // Unapplied payments are included by default, so only the off state is written to the URL.
  if (search.includeUnapplied === false) parsed.includeUnapplied = false;

  const text = parseSearchText(search.q);
  // eslint-disable-next-line id-length -- `q` is the actual URL search-param name.
  if (text) parsed.q = text;

  const view = AR_REPORT_VIEWS.find((candidate) => candidate === search.view);
  if (view && view !== 'customers') parsed.view = view;

  const { size, sort } = parseListRouteSearch(search);
  if (sort) parsed.sort = sort;
  if (size) parsed.size = size;

  return parsed;
}
